import { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  Chip,
  TableRow,
  TableCell,
  alpha,
} from '@mui/material'
import { CheckCircle, Warning, Error, Delete, ClosedCaption, Close, Storefront, MonetizationOnOutlined, CheckCircleOutlineOutlined, AccessTimeOutlined, StickyNote2Outlined } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query';
import apiService from '@/service/apiService';
import { formatCurrency } from '@/utils';
import { formatDate } from '@/utils/dateUtils';
import DataTable, { ColumnDef } from '@/components/ui/DataTable';
import ErrorHandlerAlert from '../ErrorHandlerAlert';
interface ApiResponse {
  customers: Customer[]
  summary: Summary
}

interface Customer {
  _id: string
  company: string
  vinNumber: string
  companyId: string
  companyName: string
  platformRate: PlatformRate
}

interface PlatformRate {
  autoScore: number
  stars: number
}
interface Summary {
  totalCustomers: number
  totalInvoices: number
  totalInvoiced: number
  totalReceived: number
  totalBalanceDue: number
  latePaymentsCount: number
  overdueAmount: number
  overdueCount: number
  paidCount: number
  unpaidCount: number
  onTimePaymentsCount: number
  totalReports: number
}
interface AlertCustomer {
  _id?: string;
  id?: string;
  company?: string;
  usdot?: string;
  truckDetails?: { vinNumber?: string };
  stars?: number;
}

interface AlertUserProps {
  customer: AlertCustomer;
  label?: string;
}

const companyColumns: ColumnDef[] = [
  { key: 'companyName', label: 'Company' },
  { key: 'company', label: 'Customer' },
  { key: 'usdot', label: 'USDOT', align:'center' },
  { key: 'vinNumber', label: 'VIN', align:'center' },
  { key: 'risk', label: 'Risk', align:'center' },
];

const getIcon = (stars: number) => {
  const value = stars || 0

  if (value >= 4) {
    return {
      Icon: CheckCircle,
      color: '#10853b' as const,
      label: 'Low Risk' as const,
    }
  }

  if (value >= 2.5) {
    return {
      Icon: Warning,
      color: '#f97316' as const,
      label: 'Medium Risk' as const,
    }
  }

  return {
    Icon: Error,
    color: '#c71616' as const,
    label: 'High Risk' as const,
  }
}

const AlertUser: React.FC<AlertUserProps> = ({ customer, label }) => {
  const [open, setOpen] = useState(false);
  const usdot = customer?.usdot || '';
  const vin = customer?.truckDetails?.vinNumber || '';

  const { data, isLoading,error } = useQuery({
    queryKey: ['customerPaymentAlerts', usdot, vin],
    queryFn: async () => {
      if (!usdot && !vin) return null;
      const res = await apiService.getCustomerPaymentAlerts({ usdot, vin });
      return res?.success ? res.data : null;
    },
    enabled: open && !!(usdot || vin),
    staleTime: 1000 * 60 * 2,
  });

  const { Icon, color, label: riskLabel } = getIcon(customer?.stars || 0);
  const displayLabel = riskLabel;

  const summary = data?.summary || {};
  const matchedCustomers = data?.customers || [];
  console.log("aler error",error)
  return (
    <>
      <Box
        component="span"
        title={`Payment alert details for ${customer?.company || customer?.id}`}
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color,
        }}
      >
        <Icon sx={{ fontSize: 15, color, }} />
        <Typography
          component="span"
          sx={{
            fontSize:{xs:13, md:13.4},
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {label || displayLabel}
        </Typography>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth={"md"} fullWidth>
        <DialogActions className='dialog-close'>
          <Button onClick={() => setOpen(false)}>
            <Close />
          </Button>
        </DialogActions>

        <DialogTitle sx={{px:2.5}}>
          Payment Alerts
          <Typography variant="caption" color="text.secondary" display="block">
            {customer?.company || customer?.id || 'Customer'}
            {usdot ? ` • USDOT: ${usdot}` : ''}
            {vin ? ` • VIN: ${vin}` : ''}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{px:2.5}}>
          {isLoading ? (
            <Typography color="text.secondary">Loading payment alerts...</Typography>
          ):error && error?.message?<ErrorHandlerAlert error={error}/> : (
            <>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>

                <Grid item xs={12} sm={6} md={2.3}>
                  <Card elevation={0}
                    sx={{
                    pl: 1.2,
                    pr:0,
                    py: 1,
                    display:'flex',
                    alignItems:'center',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    bgcolor:'#f5f7fc',
                    gap:1.2,
                    }}>

                    <Box sx={{bgcolor:'#dde6f8', padding:'5px', borderRadius:0.5, color:'#2062cc', border:'1px solid #a1c5ff'}}>
                      <Storefront sx={{fontSize:'24px', verticalAlign:'sub'}}/>
                    </Box>

                    <Box sx={{textAlign:'left'}}>
                    <Typography fontSize={{xs:16, md:17}} color="#2062cc" fontWeight={600}>
                      {summary.totalCustomers || 0}
                    </Typography>
                    <Typography fontSize={12.6} color="text.secondary">Shops</Typography>
                    </Box>

                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card elevation={0}
                    sx={{
                    pl: 1.2,
                    pr:0,
                    py: 1,
                    display:'flex',
                    alignItems:'center',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    bgcolor:'#f9f5fb',
                    gap:1.2,
                    }}>

                    <Box sx={{bgcolor:'#e9e0f7', padding:'5px', borderRadius:0.5, color:'#5513af', border:'1px solid #d9beff'}}>
                      <MonetizationOnOutlined sx={{fontSize:'24px', verticalAlign:'sub'}}/>
                    </Box>

                    <Box sx={{textAlign:'left'}}>
                    <Typography fontSize={{xs:16, md:17}} color="#5513af" fontWeight={600}>
                      {formatCurrency(summary.totalInvoiced || 0)}
                    </Typography>
                    <Typography fontSize={12.6} color="text.secondary">Total Amount</Typography>
                    </Box>
                  </Card>

                </Grid>

                <Grid item xs={12} sm={6} md={2.6}>
                  <Card elevation={0}
                    sx={{
                    pl: 1.2,
                    pr:0,
                    py: 1,
                    display:'flex',
                    alignItems:'center',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    bgcolor:'#f4faf7',
                    gap:1.2,
                    }}>

                    <Box sx={{bgcolor:'#d9f4e8', padding:'5px', borderRadius:0.5, color:'#0d9565', border:'1px solid #9ad5c0'}}>
                      <CheckCircleOutlineOutlined sx={{fontSize:'24px', verticalAlign:'sub'}}/>
                    </Box>

                    <Box sx={{textAlign:'left'}}>
                    <Typography fontSize={{xs:16, md:17}} color="#0d9565" fontWeight={600}>
                      {formatCurrency(summary.totalReceived || 0)}
                    </Typography>
                    <Typography fontSize={12.6} color="text.secondary">Complete Payment</Typography>
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.3}>
                  <Card elevation={0}
                    sx={{
                    pl: 1.2,
                    pr:0,
                    py: 1,
                    display:'flex',
                    alignItems:'center',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    bgcolor:'#fef8f2',
                    gap:1.2,
                    }}>

                    <Box sx={{bgcolor:'#fdedd4', padding:'5px', borderRadius:0.5, color:'#fd940e', border:'1px solid #ffc57b'}}>
                      <AccessTimeOutlined sx={{fontSize:'24px', verticalAlign:'sub'}}/>
                    </Box>

                    <Box sx={{textAlign:'left'}}>
                    <Typography fontSize={{xs:16, md:17}} color="#fd940e" fontWeight={600}>
                      {summary.latePaymentsCount || 0}
                    </Typography>
                    <Typography fontSize={12.6} color="text.secondary">Late Payment</Typography>
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card elevation={0}
                    sx={{
                    pl: 1.2,
                    pr:0,
                    py: 1,
                    display:'flex',
                    alignItems:'center',
                    border: '1px solid #ddd',
                    textAlign: 'center',
                    bgcolor:'#fdf2f3',
                    gap:1.2,
                    }}>

                    <Box sx={{bgcolor:'#fcdede', padding:'5px', borderRadius:0.5, color:'#e3393f', border:'1px solid #ffa7aa'}}>
                      <StickyNote2Outlined sx={{fontSize:'24px', verticalAlign:'sub'}}/>
                    </Box>

                    <Box sx={{textAlign:'left'}}>
                    <Typography fontSize={{xs:16, md:17}} color="#e3393f" fontWeight={600}>
                      {summary.overdueCount || 0}
                    </Typography>
                    <Typography fontSize={12.6} color="text.secondary">Overdue Invoice</Typography>
                    </Box>
                  </Card>
                </Grid>

              </Grid>

              <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight:'1' }}>
                Shops
              </Typography>

              <Box
              sx={{
                '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root':{
                  fontSize:{xs:13, md:13},
                }
              }}
              >
              <DataTable
                columns={companyColumns}
                data={matchedCustomers}
                size="small"
                stickyHeader
                minHeight={120}
                renderRow={(row: any, index: number) => {
                  const { Icon: PlatformIcon, color: platformColor, label: riskLabel } = getIcon(row.platformRate?.stars || 0);
                  return (
                    <TableRow
                      key={row._id || index}
                      sx={{
                        //bgcolor: (theme) => alpha(theme.palette[platformColor].main, 0.1),
                        '&:last-child td': { border: 0 }
                      }}
                      >
                      <TableCell sx={{fontSize:{xs:13, md:13, whiteSpace:'nowrap'}}}>{row.companyName || 'N/A'}</TableCell>
                      <TableCell sx={{fontSize:{xs:13, md:13, whiteSpace:'nowrap'}}}>{row.company || 'N/A'}</TableCell>
                      <TableCell align='center' sx={{fontSize:{xs:13, md:13},whiteSpace:'nowrap'}}>{row.usdot || 'N/A'}</TableCell>
                      <TableCell align='center' sx={{fontSize:{xs:13, md:13}, whiteSpace:'nowrap'}}>{row.vinNumber || 'N/A'}</TableCell>
                      <TableCell align='center'>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: platformColor,
                          }}
                        >
                        <PlatformIcon sx={{ fontSize: {xs:13, md:13}, color:platformColor, }}/>
                        <Typography sx={{fontSize:{xs:13, md:13}, whiteSpace:'nowrap'}} color={platformColor}>{riskLabel}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }}
              />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions className='dialog-action'>
          <Button onClick={() => setOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AlertUser
